import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

interface CacheEntry {
  expiresAt: number;
  response: HttpResponse<unknown>;
}

/**
 * In-memory store behind CacheInterceptor.
 *
 * Deliberately not persisted. The repo's posture is that auth-adjacent data never
 * reaches localStorage/sessionStorage, and every cacheable endpoint here is scoped to
 * a user, a custody mode and a chain.
 *
 * Kept separate from the interceptor so CommonService can clear it on logout without
 * depending on the interceptor (which would be a circular dependency through HttpClient).
 */
@Injectable({ providedIn: 'root' })
export class HttpCacheStore {
  private readonly entries = new Map<string, CacheEntry>();

  /** In-flight requests, so a burst of duplicate calls collapses to one network trip. */
  private readonly pending = new Map<string, Observable<HttpResponse<unknown>>>();

  get(key: string): HttpResponse<unknown> | null {
    const hit = this.entries.get(key);
    if (!hit) {
      return null;
    }
    if (Date.now() >= hit.expiresAt) {
      this.entries.delete(key);
      return null;
    }
    return hit.response;
  }

  set(key: string, response: HttpResponse<unknown>, ttlMs: number): void {
    if (ttlMs <= 0) {
      return;
    }
    this.entries.set(key, { expiresAt: Date.now() + ttlMs, response });
  }

  getPending(key: string): Observable<HttpResponse<unknown>> | null {
    return this.pending.get(key) ?? null;
  }

  setPending(key: string, request$: Observable<HttpResponse<unknown>>): void {
    this.pending.set(key, request$);
  }

  clearPending(key: string): void {
    this.pending.delete(key);
  }

  /** Called on logout so a second user on the same device never sees the first user's data. */
  clear(): void {
    this.entries.clear();
    this.pending.clear();
  }
}
